/**
 * The categories the Celebrating sheet offers, and the words that find them.
 * Used by the sheet's search and by the voice parser, so "shaadi" means
 * Wedding in both places. Plan §4 (docs/CREATE_EVENT_VOICE_PLAN.md).
 *
 * Wedding, Birthday and Holiday must stay: they are the classic form's pills,
 * and keeping them keeps category analytics comparable across both layouts.
 */

export interface CategoryOption {
  label: string;
  /** Lower-case words and phrases that should find this category. */
  synonyms: string[];
  /** Event-name placeholder while this category is picked. */
  namePlaceholder: string;
  /** Social events default a bare "at 8" to PM; work and trips to AM. */
  daytime?: boolean;
}

export const POPULAR_CATEGORIES: CategoryOption[] = [
  {
    label: 'Wedding',
    synonyms: ['marriage', 'shaadi', 'shadi', 'vivah', 'nikah', 'reception', 'mehendi', 'mehndi', 'sangeet', 'haldi', 'baraat'],
    namePlaceholder: "Alice & Steve's Wedding",
  },
  {
    label: 'Birthday',
    synonyms: ['bday', "b'day", 'birthdays', 'janamdin', 'janmdin', 'budday', 'birthday party'],
    namePlaceholder: "Anaya's 5th Birthday",
  },
  {
    label: 'Engagement',
    synonyms: ['engaged', 'sagai', 'sagaai', 'roka', 'ring ceremony'],
    namePlaceholder: "Priya & Rohan's Engagement",
  },
  {
    label: 'Anniversary',
    synonyms: ['anniversaries', 'anniv', 'salgirah'],
    namePlaceholder: "Mum & Dad's 30th",
  },
  {
    label: 'Baby shower',
    synonyms: ['babyshower', 'godh bharai', 'godbharai', 'gender reveal', 'naming ceremony', 'namkaran', 'mundan'],
    namePlaceholder: "Baby Mehta's Shower",
  },
  {
    label: 'Festival',
    synonyms: ['diwali', 'deepavali', 'holi', 'eid', 'navratri', 'garba', 'durga puja', 'ganesh chaturthi', 'ganpati',
      'christmas', 'onam', 'pongal', 'lohri', 'rakhi', 'raksha bandhan', 'karwa chauth', 'puja', 'pooja'],
    namePlaceholder: 'Diwali at the Sharmas',
  },
  {
    label: 'Party',
    synonyms: ['get together', 'get-together', 'bash', 'kitty party', 'farewell', 'dinner party', 'house party', 'office party'],
    namePlaceholder: "Sam's Farewell Party",
  },
  {
    label: 'Trip',
    synonyms: ['vacation', 'getaway', 'tour', 'travel', 'road trip', 'trek', 'picnic', 'outing', 'holiday trip'],
    namePlaceholder: 'Goa 2026',
    daytime: true,
  },
  {
    label: 'Reunion',
    synonyms: ['alumni', 'alumni meet', 'batch meet', 'class reunion', 'family reunion'],
    namePlaceholder: 'Class of 2016 Reunion',
  },
  {
    label: 'Work event',
    synonyms: ['office', 'offsite', 'off-site', 'conference', 'team outing', 'corporate', 'company', 'work party',
      'launch', 'product launch', 'meetup', 'workshop', 'seminar'],
    namePlaceholder: 'Team Offsite 2026',
    daytime: true,
  },
  {
    label: 'Graduation',
    synonyms: ['convocation', 'commencement', 'graduation party'],
    namePlaceholder: "Aarav's Graduation",
    daytime: true,
  },
  {
    label: 'Holiday',
    synonyms: ['holidays', 'new year', "new year's", 'new years', 'thanksgiving'],
    namePlaceholder: "New Year's Eve 2026",
  },
];

/**
 * The few shown up front: the sheet before anything is typed, and the
 * conversation's first question. The rest of POPULAR_CATEGORIES stays
 * reachable by search ("diwali" → Festival) and by voice, it just isn't listed.
 */
export const DEFAULT_CATEGORY_LABELS = ['Wedding', 'Birthday', 'Party', 'Holiday'];

export const DEFAULT_CATEGORIES: CategoryOption[] =
  POPULAR_CATEGORIES.filter(c => DEFAULT_CATEGORY_LABELS.includes(c.label));

/** The name placeholder when nothing (or a custom category) is picked — today's. */
export const DEFAULT_NAME_PLACEHOLDER = "Alice & Steve's Wedding";

export const MAX_CATEGORY_LENGTH = 50;

/**
 * Tidies a typed category: trims, collapses inner spaces, and capitalises the
 * first letter of an all-lowercase entry ("housewarming" → "Housewarming").
 * Anything the host capitalised themselves is kept as typed.
 */
export function normaliseCategory(raw: string): string {
  const tidy = (raw || '').replace(/\s+/g, ' ').trim().slice(0, MAX_CATEGORY_LENGTH);
  if (tidy && tidy === tidy.toLowerCase()) return tidy.charAt(0).toUpperCase() + tidy.slice(1);
  return tidy;
}

export function sameCategory(a: string, b: string): boolean {
  return (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
}

export function isPopularCategory(label: string): boolean {
  return POPULAR_CATEGORIES.some(c => sameCategory(c.label, label));
}

export function popularCategory(label: string): CategoryOption | undefined {
  return POPULAR_CATEGORIES.find(c => sameCategory(c.label, label));
}

export function namePlaceholderFor(category: string): string {
  return popularCategory(category)?.namePlaceholder ?? DEFAULT_NAME_PLACEHOLDER;
}

/** Case-insensitive de-duplication that keeps the first spelling seen. */
export function uniqueCategories(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of list) {
    const tidy = (item || '').trim();
    const key = tidy.toLowerCase();
    if (!tidy || seen.has(key)) continue;
    seen.add(key);
    out.push(tidy);
  }
  return out;
}

export interface CategorySearch {
  yours: string[];
  popular: CategoryOption[];
  /** The category an "Add …" row would add, or null when the query already exists. */
  addLabel: string | null;
}

/**
 * Filters the sheet. `yours` excludes anything that is also a popular label, so
 * a category never shows twice. A query that exactly matches an existing
 * category (case-insensitive) offers no Add row — picking it selects the existing one.
 */
export function searchCategories(query: string, yours: string[]): CategorySearch {
  const q = (query || '').trim().toLowerCase();
  const ownOnly = uniqueCategories(yours).filter(y => !isPopularCategory(y));

  const matches = (text: string) => text.toLowerCase().includes(q);
  const result: CategorySearch = {
    yours: q ? ownOnly.filter(matches) : ownOnly,
    popular: q
      ? POPULAR_CATEGORIES.filter(c => matches(c.label) || c.synonyms.some(matches))
      : DEFAULT_CATEGORIES,
    addLabel: null,
  };

  if (q) {
    const exists = ownOnly.some(y => y.toLowerCase() === q) || POPULAR_CATEGORIES.some(c => c.label.toLowerCase() === q);
    if (!exists) result.addLabel = normaliseCategory(query);
  }
  return result;
}

/**
 * Finds a category named in free text, for the voice parser. The longest
 * matching phrase wins ("birthday party" → Birthday, not Party); a tie goes to
 * the earlier mention. `extra` are the host's own categories, matched by label.
 */
export function matchCategoryInText(
  text: string,
  extra: string[] = [],
): { label: string; via: 'label' | 'synonym'; start: number; end: number } | null {
  const lower = ` ${(text || '').toLowerCase()} `;
  let best: { label: string; via: 'label' | 'synonym'; start: number; end: number; len: number } | null = null;

  const consider = (label: string, phrase: string, via: 'label' | 'synonym') => {
    const p = phrase.toLowerCase().trim();
    if (!p) return;
    const re = new RegExp(`(^|[^a-z0-9'])${escapeRegExp(p)}(?=$|[^a-z0-9])`, 'i');
    const m = re.exec(lower);
    if (!m) return;
    const start = m.index + m[1].length - 1;   // -1: the leading pad space
    const candidate = { label, via, start, end: start + p.length, len: p.length };
    if (!best || candidate.len > best.len || (candidate.len === best.len && candidate.start < best.start)) {
      best = candidate;
    }
  };

  for (const c of POPULAR_CATEGORIES) {
    consider(c.label, c.label, 'label');
    // "birthday party" names the label outright; "shaadi" is a translation.
    for (const s of c.synonyms) consider(c.label, s, s.includes(c.label.toLowerCase()) ? 'label' : 'synonym');
  }
  for (const own of uniqueCategories(extra)) {
    if (!isPopularCategory(own)) consider(own, own, 'label');
  }

  if (!best) return null;
  const { label, via, start, end } = best;
  return { label, via, start, end };
}

export function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ── recently added (for visitors without an account, and to survive offline) ──

const RECENT_KEY = 'pcg_recent_categories';

export function loadRecentCategories(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter(x => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Remembers a host-added category (last 5), newest first. */
export function rememberCategory(label: string): void {
  if (!label || isPopularCategory(label)) return;
  try {
    const next = uniqueCategories([label, ...loadRecentCategories()]).slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked (private mode) — the category still applies to this event.
  }
}
